import errorMonitor from '@/infra/tracking/error-monitor'
import { Spinner } from '@/presentation/components'
import GenericError from '@/presentation/components/ui/feedback/generic-error'
import WhatsAppButton from '@/presentation/components/ui/button/whatsapp-button'
import AppProviders from '@/presentation/contexts/app-providers'
import useAccount from '@/presentation/hooks/auth/use-account'
import RootLayout from '@/presentation/components/ui/layout/root-layout'
import { createBrowserRouter, RouterProvider, RouteObject } from 'react-router-dom'
import { useMemo } from 'react'

import { makeLogoutRoute, PublicRoutes } from './modules/auth/routes'
import { UserRoutes } from './modules/user/routes'
import { BackofficeRoutes } from './modules/backoffice/routes'
import { BackofficeCompatRedirects } from './modules/backoffice/compat'
import { Account } from '@/domain/usecases'

// ============================================
// ROUTER PROVIDER COMPONENT
// ============================================

/**
 * Creates and provides the router instance
 * Memoized to avoid recreating the router on every render
 */
function RouterProviderComponent({ routes }: { routes: RouteObject[] }) {
  const router = useMemo(() => {
    const wrappedCreateBrowserRouter = errorMonitor.wrapCreateBrowserRouter(createBrowserRouter)

    return wrappedCreateBrowserRouter(
      [
        {
          element: <RootLayout />,
          errorElement: <Spinner fullScreen />,
          children: routes
        }
      ],
      {
        // Enable React Router v7 features
        future: {
          v7_normalizeFormMethod: true
        }
      }
    )
  }, [routes])

  return (
    // No v7_startTransition: navigation must swap immediately and show the
    // route's loading/skeleton fallback while data loads, instead of holding
    // the previous page on screen until the new one is fully ready.
    <RouterProvider router={router} fallbackElement={<Spinner fullScreen />} />
  )
}

// ============================================
// ROTAS AUTENTICADAS (compostas a partir dos módulos de role)
// ============================================

function AuthenticatedRoutes(account: Account.Model): RouteObject[] {
  return [
    makeLogoutRoute(),
    ...UserRoutes(account),
    ...BackofficeRoutes(account),
    ...BackofficeCompatRedirects()
  ]
}

// ============================================
// ROUTER FACTORY
// ============================================

function RouterFactory() {
  const [account] = useAccount()
  // Aguarda o carregamento da conta
  if (!account) {
    console.log('[RouterFactory] ⏳ Waiting for account...')
    return <Spinner fullScreen />
  }

  console.log('[RouterFactory] ✅ Account loaded')

  // Combina todas as rotas (públicas/auth + módulos autenticados)
  const routes = [
    {
      children: PublicRoutes(),
      errorElement: <Spinner fullScreen />
    },
    {
      children: AuthenticatedRoutes(account),
      errorElement: <GenericError fullScreen />
    },
    {
      path: '*',
      element: <GenericError fullScreen />
    }
  ]

  return <RouterProviderComponent routes={routes} />
}

// ============================================
// MAIN ROUTER COMPONENT
// ============================================

export default function Router() {
  return (
    <AppProviders>
      <RouterFactory />
      <WhatsAppButton />
    </AppProviders>
  )
}
